import { useCallback } from 'react';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { FF_WEAVE_FIGMA_CREDIT_RATIO, FF_WEAVE_FILE_FIGMA_CREDITS_CONSUMPTION } from '@/consts/featureFlags';
import {
  useFigmaEmbedAiMeterHasCredits,
  useFigmaEmbedAiMeterResetsAt,
  useIsFigmaEmbedMode,
} from '@/contexts/FigmaEmbedModeContext';
import type { CreditLimitWindowStatus, FigmaAiMeterStatus } from '@/api/credits/credits.api.types';

/**
 * Fallback Weavy→Figma AI credit ratio, used when the `weave-figma-credit-ratio`
 * remote config is absent or malformed. Mirrors the backend
 * `DEFAULT_WEAVY_TO_FIGMA_CREDIT_RATIO`. Prefer {@link useWeavyToFigmaCredits},
 * which reads the live remote-config value.
 */
export const DEFAULT_WEAVY_TO_FIGMA_CREDIT_RATIO = 1.614;

/**
 * Converts a Weavy AI credit amount to its Figma AI credit equivalent, rounded
 * to the nearest whole credit. Internal to this module — React callers use
 * {@link useWeavyToFigmaCredits}, which supplies the live remote-config ratio.
 */
const weavyToFigmaCredits = (weavyCredits: number, ratio: number = DEFAULT_WEAVY_TO_FIGMA_CREDIT_RATIO): number =>
  Math.round(weavyCredits * ratio);

/**
 * Returns a converter from Weavy AI credits to Figma AI credits driven by the
 * `weave-figma-credit-ratio` PostHog remote config (bare-number payload).
 *
 * Mirrors the backend `FigmaCreditStrategy.toFigmaCredits`: a non-positive or
 * absent payload falls back to {@link DEFAULT_WEAVY_TO_FIGMA_CREDIT_RATIO}, so
 * the credits shown here match the cost the server charges.
 */
export const useWeavyToFigmaCredits = (): ((weavyCredits: number) => number) => {
  const { variantData } = useFeatureFlag(FF_WEAVE_FIGMA_CREDIT_RATIO);
  const ratio = typeof variantData === 'number' && variantData > 0 ? variantData : DEFAULT_WEAVY_TO_FIGMA_CREDIT_RATIO;
  return useCallback((weavyCredits: number) => weavyToFigmaCredits(weavyCredits, ratio), [ratio]);
};

/**
 * The EAP credit-limit window (if any) that would block a run of the given cost —
 * i.e. a window whose remaining credits are less than the run cost. When more
 * than one window blocks, returns the one that resets LAST (the true unblock
 * time). Returns undefined when nothing blocks (or cost/windows are unknown).
 */
export const getBlockingCreditWindow = (
  windows: CreditLimitWindowStatus[] | undefined,
  cost: number | undefined,
): CreditLimitWindowStatus | undefined => {
  if (!windows?.length || typeof cost !== 'number') {
    return undefined;
  }
  const blocking = windows.filter((w) => w.remaining < cost);
  if (!blocking.length) {
    return undefined;
  }
  return blocking.reduce((furthest, w) => (new Date(w.resetAt) > new Date(furthest.resetAt) ? w : furthest));
};

interface FigmaEmbedCreditGate {
  /** True when Figma's AI meter is the authoritative credit gate (embed + consumption flag on). */
  gateOnFigmaMeter: boolean;
  figmaMeterStatus: FigmaAiMeterStatus;
  /** The credit-limit window blocking the run. */
  effectiveBlockingWindow: CreditLimitWindowStatus | undefined;
  /** Whether credits block the run, resolved for the active regime. */
  creditsBlocked: boolean;
}

/**
 * Shared credit gate for the embed run-model surfaces (ModelNodeFooter and
 * RunModelsSection).
 */
export function useFigmaEmbedCreditGate(
  blockingCreditWindow: CreditLimitWindowStatus | undefined,
  notEnoughCredits: boolean,
): FigmaEmbedCreditGate {
  // Check if we are in Figma embed mode and if the consumption flag is on.
  const isFigmaEmbedMode = useIsFigmaEmbedMode();
  const { value: chargesFigmaCredits } = useFeatureFlag(FF_WEAVE_FILE_FIGMA_CREDITS_CONSUMPTION);

  const figmaAiMeterHasCredits = useFigmaEmbedAiMeterHasCredits();
  const figmaAiMeterResetsAt = useFigmaEmbedAiMeterResetsAt();

  const gateOnFigmaMeter = isFigmaEmbedMode && !!chargesFigmaCredits;
  const figmaMeterBlocked = gateOnFigmaMeter && figmaAiMeterHasCredits === false;
  const figmaMeterStatus: FigmaAiMeterStatus = {
    type: 'figma_credits',
    blocked: figmaMeterBlocked,
    resetAt: figmaAiMeterResetsAt,
  };

  const effectiveBlockingWindow = gateOnFigmaMeter ? undefined : blockingCreditWindow; // weave credit consumption concept
  const creditsBlocked = gateOnFigmaMeter ? figmaMeterBlocked : notEnoughCredits;
  return { gateOnFigmaMeter, figmaMeterStatus, effectiveBlockingWindow, creditsBlocked };
}
